import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import { Button, Checkbox } from '@fourthwall/components'

import type { App, AppScopeType } from '../../types'
import { SCOPE_GROUPS } from './ScopesTab.consts'
import * as S from './ScopesTab.styled'
import { useUpdatePlatformAppClientScopes } from './hooks/useUpdatePlatformAppClientScopes'

type ScopesFormValues = {
  scopes: AppScopeType[]
}

type ScopesTabProps = {
  app: App
}

const validationSchema = Yup.object().shape({
  scopes: Yup.array().min(1, 'At least one scope must be selected'),
})

const getInitialValues = (app: App): ScopesFormValues => ({
  scopes: app?.scopes || [],
})

export const ScopesTab = ({ app }: ScopesTabProps) => {
  const { updatePlatformAppClientScopes, isLoading } = useUpdatePlatformAppClientScopes()
  const handleScopesSave = (scopes: AppScopeType[]) => {
    if (!app.id) return
    updatePlatformAppClientScopes([{ clientId: app.id }, { scopes }])
  }

  const handleSubmit = (values: ScopesFormValues) => {
    handleScopesSave(values.scopes)
  }

  return (
    <Formik<ScopesFormValues>
      initialValues={getInitialValues(app)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, values, setFieldValue }) => {
        const currentScopes: AppScopeType[] = values.scopes || []

        const isScopeSelected = (scopeValue: string) => {
          return currentScopes.includes(scopeValue as AppScopeType)
        }

        const toggleScope = (scopeValue: string, checked: boolean) => {
          const scope = scopeValue as AppScopeType

          if (checked) {
            setFieldValue('scopes', [...currentScopes, scope])
          } else {
            setFieldValue(
              'scopes',
              currentScopes.filter((s) => s !== scope),
            )
          }
        }

        return (
          <Form>
            <S.Container>
              <S.Description>
                Select the permissions your app needs to function properly. Only request the scopes
                that are necessary for your app's functionality.
              </S.Description>

              <S.GridContainer>
                {SCOPE_GROUPS.map((group) => (
                  <S.GroupContainer key={group.header}>
                    <S.GroupHeader>
                      <S.GroupTitle>{group.header}</S.GroupTitle>
                      {group.description && (
                        <S.GroupDescription>{group.description}</S.GroupDescription>
                      )}
                    </S.GroupHeader>
                    <S.TypesContainer>
                      <S.ReadWriteContainer>
                        {group.scopes.map((scope) => (
                          <S.ReadWritePair key={scope.value}>
                            <Checkbox
                              value={isScopeSelected(scope.value) || false}
                              onValueChange={(v) => toggleScope(scope.value, v)}
                              size="small"
                            />
                            <S.ReadWriteLabel>{scope.label}</S.ReadWriteLabel>
                          </S.ReadWritePair>
                        ))}
                      </S.ReadWriteContainer>
                    </S.TypesContainer>
                  </S.GroupContainer>
                ))}
              </S.GridContainer>

              <S.ButtonContainer>
                <Button
                  label="Save Changes"
                  appearance="primary"
                  onClick={submitForm}
                  disabled={isLoading}
                  loading={isLoading}
                />
              </S.ButtonContainer>
            </S.Container>
          </Form>
        )
      }}
    </Formik>
  )
}
